import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StarIcon from '../icons/StarIcon';
import ReturnIcon from '../icons/ReturnIcon';
import HeartIcon from '../icons/HeartIcon';
import { Rate } from 'antd';
import { useAppStore } from '../store/Store';

import { BASE_URL } from '../apis/constant';

const Tag = ({ text, color }) => {
  const baseClasses = 'absolute top-3 left-3 px-2 py-0.5 text-xs font-bold rounded-full text-white tracking-wider z-10';



  return <div className={`${baseClasses} ${color}`}>{text}</div>;
};

const PriceDisplay = ({ price, offerPrice }) => (
  <div className="flex items-center gap-3">
    <span className="text-lg sm:text-xl font-bold text-gray-900">
      {`${(offerPrice ?? price).toLocaleString('en-US', { maximumFractionDigits: 0 })}৳`}
    </span>
    {offerPrice && (
      <div className="flex items-center gap-1">
        <span className="text-xs sm:text-sm text-gray-400 line-through">
          {`${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}৳`}
        </span>
      </div>
    )}
  </div>
);


export const ProductCard = ({ product }) => {
  const { id, name, imageUrl, price, offerPrice, tag, variants, rating, reviews } = product;

  const navigate = useNavigate();


  // Zustand state and actions
  const {
    wishlistItems,
    toggleWishlist,
    openProductDrawer
  } = useAppStore();

  const isWishlisted = wishlistItems.some(item => item.id === id);

  const isActionDisabled = tag?.type === 'Sold Out' || tag?.type === 'comingSoon';

  const buttonText = useMemo(() => {
    if (tag?.type === 'Sold Out') return 'Sold Out';
    if (tag?.type === 'comingSoon') return 'Coming Soon';
    if (variants && variants.length > 0) return 'Choose Options';
    return 'Order Now';
  }, [tag, variants]);

  const hasVariants = useMemo(() => {
    return variants && variants.length > 0;
  }, [variants]);

  const handleWishlistToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product);
  };

  const slugify = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "");



  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isActionDisabled) return;

    if (hasVariants) {
      // Redirect to product detail page for products with variants
      navigate(`/shop/${id}/${slugify(name)}`);
    } else {
      // Open drawer for simple products without variants
      openProductDrawer(product);
    }
  };


  const handleProductClick = (e) => {
    e.preventDefault();
    navigate(`/shop/${id}/${slugify(name)}`);
  };

  return (
    <div
      className="w-full bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 ease-in-out border border-gray-100 overflow-hidden flex flex-col cursor-pointer"
      onClick={handleProductClick}
    >
      <div className="relative overflow-hidden">
        {tag && <Tag text={tag.text} color={tag.color} />}

        {/* Wishlist Button */}
        <button
          onClick={handleWishlistToggle}
          className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/20 backdrop-blur-sm text-gray-800 hover:text-red-500 transition-all duration-300 hover:scale-110"
          aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
        >
          <HeartIcon isFilled={isWishlisted} />
        </button>

        {/* Product Image */}
        <img
          src={`${BASE_URL}${imageUrl}`}
          alt={name}
          className="w-full h-auto aspect-square object-cover transition-transform duration-300 hover:scale-105"
        />

      </div>

      {/* Product Info */}
      <div className="p-3 flex flex-col flex-grow pt-">
        <div className="text-sm sm:text-[14px] font-bold text-gray-800 truncate" title={name}>
          {name}
        </div>

        <div className="flex items-center gap-1 mt-0.5">
          {/* Stars */}
          <div className="flex items-center gap-[2px]">
            {[...Array(5)].map((_, index) => {
              let fillPercent = 0;

              if (rating && rating > 0) {
                const starValue = rating - index;
                if (starValue >= 0.9) fillPercent = 100;
                else if (starValue >= 0.4) fillPercent = 70;
                else if (starValue >= 0.1) fillPercent = 30;
              }

              return (
                <div key={index} className="relative w-3 h-3">
                  <StarIcon className="absolute top-0 left-0 w-3 h-3 text-gray-300" />
                  {fillPercent > 0 && (
                    <div
                      className="absolute top-0 left-0 h-3 overflow-hidden"
                      style={{ width: `${fillPercent}%` }}
                    >
                      <StarIcon className="w-3 h-3 text-yellow-400" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Text */}
          <span className="text-[11px] font-semibold text-gray-700">
            {reviews > 0 ? rating.toFixed(1) : ''}
          </span>
          <span className="text-[11px] text-gray-500">
            {reviews > 0 ? `(${reviews})` : '(Not Rated)'}
          </span>
        </div>




        {/* Price and Return Policy */}
        <div className="mt-auto">
          <PriceDisplay price={price} offerPrice={offerPrice} />
          <div className="flex items-center gap-1 mt-1 text-[10px] font-medium text-emerald-600">
            <ReturnIcon className="w-3 h-3 flex-shrink-0" />
            <span>3 days easy return policy</span>
          </div>
        </div>
      </div>

      {/* Add to Cart Button */}
      <div className="px-3 pb-3">
        <button
          onClick={handleButtonClick}
          disabled={isActionDisabled}
          className="w-full px-4 py-2 text-xs sm:text-sm font-bold bg-gray-900 text-white rounded-lg transition duration-300 ease-in-out hover:bg-gray-700 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-gray-900"
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
